import axios from "axios";
import apiConfig from "../service/apiConfig";
import {
  checkDomainCode,
  validateZipCode,
} from "../common/components/Helper/Helper";
import {
  AppConstants,
  LOCAL_HEADER_LOGO_ID,
  MLY_IDS,
  MULTIPLE_LOCATIONS_ERROR_MSG,
  NBLY_DEV,
  NBLY_PROD,
  NBLY_TEST,
  serviceDetailsLabels,
  SESSION_CALENDARFLOW,
  SESSION_FORMVALUES,
  stateAbbreviations,
} from "../constants/constant";

import ContactUsPayloadInterface from "./model/ContactUsPayloadInterface";
import ContactUsPayload from "../feature/scheduler/components/Helper/ContactUsPayload";
import {
  generateTechAvailablityAPIUrl,
  sessionStorageUtils,
} from "../utility/utility";
import AtomAPI from "./apis/atomAPI";
import { VinAPI } from "./model/vehicle";
import { AtomAPIResponse } from "./model/atomAPIResponse";
import {
  WDG_FORM_VALUES_HOW_YOU_COUNT_SQFT_KEY,
  WDG_GUTTER_PROPERTY_SIZE,
  WDG_PROPERTY_SIZE,
} from "../feature/quotation/Constants/constants";
import {
  CalculateEstimatePayload,
  FormValues,
  IsReturningCustomerPayload,
  IsReturningCustomerResponse,
  OffersRequestPayload,
  SchedulerConfig,
} from "../Interface/scheduler/form";
import isInboundFromNbly from "../utility/checkInBoundFromNbly";

export const fetchFranchiseLookupByServiceAddress = async (
  address: string,
  conceptID: string,
  serviceid?: string,
  isRoundRobin?: boolean,
) => {
  try {
    let URL = `${apiConfig.franchiseLookUpServiceAddress}/${address || ""}/${isRoundRobin || false}/true/${conceptID || 0}`;
    if (serviceid) {
      URL += `/${serviceid}`;
    }
    const response = await globalThis.GetCachedResponse(URL);
    return response;
  } catch (err: any) {
    if (typeof err?.then === "function") {
      err = await err;
    }
    const message = err?.response?.data?.message || err?.message || "";
    return message === MULTIPLE_LOCATIONS_ERROR_MSG ? err : { err: true };
  }
};

//To DO - remove this fetchAddressSuggestions and use existing hook "useApiResults" for getting address suggestions

export const fetchAddressSuggestions = async (
  name: string,
  address: string,
  zipcode: string,
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>,
  setIsLoading: (loading: boolean) => void,
) => {
  try {
    let geoLocation;
    setIsLoading(true);
    if (validateZipCode(zipcode)) {
      geoLocation = await fetchGeoLocation(zipcode);
      const firstItem = geoLocation?.items?.[0];
      if (firstItem && name === "address") {
        const { address } = firstItem;
        setFormValues((prevValues) => ({
          ...prevValues,
          city: address.city,
          state: address.state,
          countryCode: address.countryCode,
          attomAPIDataReceived: false,
          [WDG_FORM_VALUES_HOW_YOU_COUNT_SQFT_KEY]: "",
          [WDG_PROPERTY_SIZE]: "",
          [WDG_GUTTER_PROPERTY_SIZE]: "",
          ...(setIsLoading.length > 0
            ? {
                [MLY_IDS.MLY_HOME_PROPERTY_SQ_FT]: "",
                [MLY_IDS.MLY_OFFICE_PROPERTY_SQ_FT]: "",
                [MLY_IDS.MLY_RES_BEEDROOMS]: "",
                [MLY_IDS.MLY_RES_BATHROOMS]: "",
                [MLY_IDS.MLY_COM_OFFICES]: "",
                [MLY_IDS.MLY_COM_BATHROOMS]: "",
              }
            : {}),
        }));
      }
    }
    const circle = geoLocation?.items[0]?.position;
    const at = circle
      ? `&in=circle:${circle.lat},${circle.lng};r=50000`
      : "&at=0,0";
    const discoverData = await axios.get(
      `${apiConfig.discoveryApiURL}?q=${address}&in=countryCode%3AUSA%2CCAN` +
        `${at}&apiKey=${apiConfig.DISCOVER_API_KEY}`,
    );

    setIsLoading(false);
    return discoverData?.data?.items;
  } catch (error) {
    console.error("Error fetching address suggestions:", error);
    setIsLoading(false);
  }
};

export const fetchGenericLeadSource = async (setHowAboutUsOptions, params) => {
  try {
    const URL = `${apiConfig.genericLeadSource}?apikey=${apiConfig.API_KEY}${params !== "" ? params : ""}`;
    const response = await globalThis.GetCachedUnifiedSync(URL);
    setHowAboutUsOptions(response);
  } catch (error) {
    console.error("Error fetching 'how-about-us' options:", error);
  }
};

export const fetchSyncSourceLeadSource = async (setHowAboutUsOptions) => {
  try {
    const URL = `${apiConfig.syncSourceLeadTypeThree}?apikey=${apiConfig.API_KEY}`;
    const response = await globalThis.GetCachedUnifiedSync(URL);
    setHowAboutUsOptions(response);
  } catch (error) {
    console.error(
      "Error fetching 'sync-source-lead-type-three' options:",
      error,
    );
  }
};

export const fetchGeoLocation = async (zipcode) => {
  try {
    const URL = `${apiConfig.geoLocationURL}?q=${zipcode}&apiKey=${apiConfig.DISCOVER_API_KEY}&in=countryCode%3AUSA%2CCAN`;
    const response = await globalThis.GetCachedUnifiedSync(URL);
    return response;
  } catch (error) {
    console.error("Error fetching geo location:", error);
  }
};

export const fetchSiteConfig = async (brandUrl) => {
  try {
    const url = `${brandUrl}/config-files/_site-config.json`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching site config:", error);
  }
};

export const fetchBrandsJson = async (brandId) => {
  const URL = globalThis.location.origin + globalThis.location.pathname;
  const isNBLY = isInboundFromNbly();
  const isCASite = isNBLY
    ? URL.includes("com/ca")
    : URL.includes("-ca") || URL.includes(".ca");
  const brandsURL = isCASite
    ? apiConfig.brandCANJsonURL
    : apiConfig.brandJsonURL;
  try {
    const response = await axios.get(
      `${brandsURL}?brandId=${brandId}&apiKey=${apiConfig.API_KEY}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching brand json:", error);
  }
};

export const fetchApplianceBrandJson = async () => {
  try {
    const response = await axios.get(apiConfig.applianceBrandURL);
    return response.data;
  } catch (error) {
    console.error("Error fetching brand json:", error);
  }
};

export const fetchServiceQuestions = async () => {
  try {
    const response = await globalThis.GetCachedUnifiedSync(
      apiConfig.wdgAgnosticUrl,
    );
    return response;
  } catch (error) {
    console.error("Error fetching agnostic json:", error);
  }
};

export const fetchSchedulingByFullAttribute = async (webLocationId) => {
  try {
    const URL = `${apiConfig.fullAttributeURL}?franchiseWeblocationId=${webLocationId}&apikey=${apiConfig.API_KEY}`;
    const response = await globalThis.GetCachedUnifiedSync(URL);
    return response;
  } catch (error) {
    console.error("Error while fetching online scheduling", error);
  }
};

export const MIN_DAYS_FOR_CALENDAR_DATE_SEEK = 14;

export const isCalendarDateSeekEnabled = (
  formConfig: SchedulerConfig | undefined,
): boolean => {
  return (
    Boolean(formConfig?.enableCalendarDateSeek) ||
    (Array.isArray(formConfig?.steps) &&
      formConfig.steps.some((step) =>
        Boolean(step?.enableCalendarDateSeek),
      ))
  );
};

export const fetchTechAvailability = async (
  locationId,
  startDate,
  days,
  formValues,
  formConfig,
  wdgServiceAccordionNonPriciableSkuId,
) => {
  const isShgConcept =
    String(formValues?.conceptID ?? formConfig?.conceptid ?? "") === "26";
  const enableCalendarDateSeek = isCalendarDateSeekEnabled(formConfig);

  const effectiveDays = enableCalendarDateSeek
    ? Math.min(days, MIN_DAYS_FOR_CALENDAR_DATE_SEEK)
    : days;

  try {
    const url = generateTechAvailablityAPIUrl(
      formValues,
      formConfig,
      locationId,
      startDate,
      effectiveDays,
      wdgServiceAccordionNonPriciableSkuId,
    );
    const elements = formConfig?.steps.find(
      (step) => step?.steptype === AppConstants.HOW_WE_CAN_REACH,
    )?.elements;
    let isShelfCloset = false;
    if (elements) {
      elements?.forEach((element) => {
        if (element.type === AppConstants.AGNOSTIC_FORM) {
          element?.fields?.forEach((field) => {
            if (field.id === AppConstants.SHELF_STORAGE_ID) {
              isShelfCloset = true;
            } else if (
              formValues?.shelfStorageConfigure &&
              formValues.offersClosetAppointments === false
            ) {
              isShelfCloset = true;
            }
          });
        }
      });
    }
    if (isShelfCloset) {
      const payload = formValues?.shelvingstorage
        ? {
            ShgAppointmentType: formValues?.shelvingstorage,
            vendorId: formConfig?.vendorId,
          }
        : {
            ShgAppointmentType: AppConstants.SHELF_CLOSET_DEFAULT,
            vendorId: formConfig?.vendorId,
          };
      const endPointUrl = `${url}&ShgAppointmentType=${payload.ShgAppointmentType}&vendorId=${payload.vendorId}`;
      const response = await globalThis.GetCachedUnifiedSync(endPointUrl);
      return response;
    } else {
      const payload = {
        VendorId: formConfig?.vendorId,
      };
      const endPointUrl = `${url}&VendorId=${payload.VendorId}`;
      const response = await globalThis.GetCachedUnifiedSync(endPointUrl);
      return response;
    }
  } catch (error) {
    console.error("Error while fetching tech availablity", error);
  }
};
export const leadSourceApi = async (setHowAboutUsOptions, conceptId) => {
  try {
    const URL = `${apiConfig.leadSource}${conceptId}/lead-sources?apikey=${apiConfig.API_KEY}`;
    const response = await globalThis.GetCachedUnifiedSync(URL);
    setHowAboutUsOptions(response);
  } catch (error) {
    console.error("Error while fetching lead sources", error);
  }
};

export const appendCountryCodeToUrl = (url, countryCode) => {
  if (
    url.includes(`-${countryCode}-`) ||
    url.includes(`-${countryCode}.`) ||
    url.includes(`.${countryCode}`)
  ) {
    return `${url}/`;
  }
  const urlObj = new URL(url);
  const hostnameParts = urlObj.hostname?.split(".");
  const subdomainParts = hostnameParts[0]?.split("-");

  if (url.includes(NBLY_DEV) || url.includes(NBLY_TEST)) {
    subdomainParts?.splice(subdomainParts.length - 1, 0, countryCode);
  } else if (url?.includes(NBLY_PROD)) {
    subdomainParts.push(countryCode);
  } else {
    hostnameParts[hostnameParts.length - 1] = countryCode;
  }

  hostnameParts[0] = subdomainParts?.join("-");
  urlObj.hostname = hostnameParts?.join(".");
  return urlObj.toString();
};

export const fetchDynamicMenuJson = async (
  formValues,
  multipleDomain,
  identiFyZipCode,
) => {
  const dbaName = localStorage.getItem("dbaName");
  const isUSZipCode = identiFyZipCode === AppConstants.COUNTRY_US;
  try {
    let url = `${apiConfig.dynamicMenu}/${formValues.franchiseWebLocationId}/${dbaName}/true`;
    if (isInboundFromNbly) {
      url += `?leadconcept=${formValues.conceptCode}`;
    }
    if (multipleDomain) {
      if (identiFyZipCode === AppConstants.COUNTRY_CA) {
        url = appendCountryCodeToUrl(
          globalThis.location.origin,
          identiFyZipCode,
        );
        url = `${url}cesmenu/${
          formValues.franchiseWebLocationId
        }/${dbaName}/true`;
      } else if (checkDomainCode() === AppConstants.COUNTRY_CA && isUSZipCode) {
        const domain = globalThis.location.origin
          .replace(/-ca-|-ca/g, "-")
          .replace(/\.ca/g, ".com");
        url = `${domain}/cesmenu/${formValues.franchiseWebLocationId}/${dbaName}/true`;
      }
    }
    const response = await globalThis.GetCachedUnifiedSync(url);
    return response.serviceMenu.country[0].brand[0].webLocation[0].option;
  } catch (error) {
    console.error("Error while fetching online scheduling", error);
  }
};

export const WebLocationsAPI = async (conceptCode: string, formValues) => {
  try {
    const baseUrl = `${apiConfig.WebLocationsAPI}/${conceptCode}/franchiseweblocations`;
    const url = formValues?.userInfoServiceId
      ? `${baseUrl}?serviceId=${formValues.userInfoServiceId}`
      : baseUrl;
    const response = await axios.get(`${url}`, {
      params: {
        apikey: apiConfig.API_KEY,
      },
    });
    return response;
  } catch (error) {
    console.error("Error fetching franchise lookup:", error);
    throw error;
  }
};

export const fetchFranchisePosLookup = async (
  franchiseWebLocationId: string,
) => {
  try {
    const url = `${apiConfig.franchisePosLookup}${franchiseWebLocationId}`;
    const response = await globalThis.GetCachedResponse(url);
    return response;
  } catch (error) {
    console.error("Error calling POS Lookup API:", error);
    throw error;
  }
};

export const fetchISNRedirectConfigURLs = async (conceptCode) => {
  try {
    let url = `${apiConfig.isnRedirectConfigUrl}`;
    if (isInboundFromNbly) {
      url = `${apiConfig.isnRedirectConfigUrl}?leadconcept=${conceptCode}`;
    }
    const response = await globalThis.GetCachedResponse(url);
    return response;
  } catch (error) {
    console.error("Error fetching ISN Redirect Config URLs:", error);
    throw error;
  }
};

export const WebLocationsMilesEndpoint = async (
  conceptCode: string,
  formValues: FormValues,
  franchiseLimit: number,
) => {
  try {
    if (!formValues?.zipcode) return;

    const baseUrl = `${apiConfig.WebLocationsAPI}/${conceptCode}/franchiseweblocations/${formValues.zipcode}/miles/${franchiseLimit}`;
    const url = formValues?.userInfoServiceId
      ? `${baseUrl}?serviceId=${formValues.userInfoServiceId}`
      : baseUrl;

    const response = await axios.get(`${url}`, {
      params: {
        apikey: apiConfig.API_KEY,
      },
    });
    return response?.data ?? [];
  } catch (error) {
    console.error("Error fetching franchise lookup:", error);
    throw error;
  }
};

export const feeInspection = async (
  zipcode: string,
  webLocationId: string,
  endPointType: string,
) => {
  try {
    const URL =
      endPointType !== AppConstants.INSPECTION_FEE_WITHOUT_ZIPCODE
        ? `${apiConfig.feeInspection}/${webLocationId}/${zipcode}?apikey=${apiConfig.API_KEY}`
        : `${apiConfig.feeInspectionWithOutZipCode}${webLocationId}`;
    const response =
      endPointType !== AppConstants.INSPECTION_FEE_WITHOUT_ZIPCODE
        ? await globalThis.GetCachedUnifiedSync(URL)
        : await globalThis.GetCachedResponse(URL);
    return response;
  } catch (error) {
    console.error("Error fetching fee inspection details ", error);
  }
};

export const bookingApiCall = async (url, payload) => {
  try {
    const finalURL = url + `&step=${payload.stepType}`;
    const response = await globalThis.BookingWrapper(finalURL, payload);
    return response;
  } catch (error) {
    console.error("Error while booking appointment", error);
  }
};

export const fetchCrossSellServices = async (payload) => {
  try {
    const response = await axios.post(apiConfig.crossSellApi, payload, {
      headers: {
        "Content-Type": "application/json",
        apikey: apiConfig.API_KEY,
      },
    });
    return response.data;
  } catch (error) {
    console.log("Error while fetching Cross Sell Services", error);
  }
};

export async function fetchVehicleDetails(payload): Promise<VinAPI> {
  try {
    const vehicleDetails = await axios.post(apiConfig.vehicleDetails, payload, {
      headers: {
        "Content-Type": "application/json",
        apikey: apiConfig.API_KEY,
      },
    });
    return vehicleDetails.data;
  } catch (error) {
    console.error("Error fetching vehicle details:", error);
  }
}
const storeFormDataAndRedirect = (formValues, formConfig) => {
  globalThis.onbeforeunload = null;
  const redirectionUrl = formConfig?.redirectionPageUrl ?? "/confirmation";
  sessionStorageUtils.setItem(SESSION_FORMVALUES, formValues);
  sessionStorageUtils.setItem(SESSION_CALENDARFLOW, "false");
  if (isInboundFromNbly()) {
    const baseUrl = globalThis.location.origin;
    globalThis.location.replace(
      `${baseUrl}${redirectionUrl}?leadconcept=${formConfig.conceptCode}`,
    );
    return;
  }
  globalThis.location.replace(redirectionUrl);
};

export const fetchPropertyDetails = async (
  houseNumber: string,
  streetAddress: string,
  city: string,
  state: string,
  zip: string,
  country: string,
  vendorId: string,
): Promise<AtomAPIResponse> => {
  try {
    const atomAPI = new AtomAPI();
    const response = await atomAPI.fetchPropertyDetails(
      houseNumber,
      streetAddress,
      city,
      state,
      zip,
      country,
      vendorId,
    );
    return response;
  } catch (error) {
    console.error(
      "Error fetching Property Details in Digital Orchestration:",
      error,
    );
    throw error;
  }
};

export const ContactUsApiCallHandler = async (
  formValues: FormValues,
  formConfig: SchedulerConfig,
  correlationId: string,
) => {
  sessionStorage.removeItem("serviceDescription");
  sessionStorageUtils.setItem("contactUsTriggered", "true");
  try {
    const contactUsPayload: ContactUsPayloadInterface = ContactUsPayload(
      formValues,
      formConfig,
      correlationId,
    );
    const serviceType = formValues?.servicetype;
    const selectedServiceType =
      serviceType?.charAt(0)?.toUpperCase() + serviceType?.slice(1);
    const serviceTypeText = `${serviceDetailsLabels?.SERVICE_TYPE}: ${selectedServiceType}`;
    const leadSourceText = `${serviceDetailsLabels?.LEAD_SOURCE_TEXT}: ${formValues.leadSourceName}`;
    let finalComments = formValues?.osflow_contactUs_comments;
    if (serviceType)
      finalComments = finalComments
        ? `${finalComments} | ${serviceTypeText}`
        : serviceTypeText;
    if (formValues?.leadSourceId) {
      finalComments = finalComments
        ? `${finalComments} | ${leadSourceText}`
        : leadSourceText;
    }
    const selectedOffer = sessionStorage.getItem("offerDetails");
    if (selectedOffer) {
      const offer = JSON.parse(selectedOffer);
      const offerText = `${AppConstants.SPECIAL_OFFER_TEXT}: ${offer.title}, ${offer.description}, ${offer.couponexpirydate || ""}, ${offer.couponcode || ""}, ${offer.longdescription || ""}`;
      finalComments = finalComments
        ? `${finalComments} | ${offerText}`
        : offerText;
    }
    contactUsPayload.comments = finalComments;
    contactUsPayload.vendorId = Number(formConfig.vendorId);
    const URL = `${apiConfig.contactUsSend}?apikey=${apiConfig.API_KEY}`;
    await globalThis.ContactWrapper(URL, contactUsPayload);
    storeFormDataAndRedirect(formValues, formConfig);
  } catch (error) {
    console.error("Error posting data:", error);
  }
};

export const getLocalMenuHeaderLogo = async (webLocation) => {
  try {
    const dbaName = localStorage.getItem("dbaName");
    const URL = `${globalThis.location.origin}/localmenu/${webLocation}/${dbaName}`;
    const response = await globalThis.GetCachedUnifiedSync(URL);
    const parser = new DOMParser();
    const doc = parser.parseFromString(response, "text/html");
    if (doc) {
      const logoImagePath = doc
        .getElementById(LOCAL_HEADER_LOGO_ID)
        ?.textContent?.trim();
      return logoImagePath && `${globalThis.location.origin}/${logoImagePath}`;
    }
  } catch (error) {
    console.error("Error fetching local menu header logo:", error);
  }
};

export const getPricingByWebLocation = async (payloadObject) => {
  try {
    const response = await globalThis.PriceQuoteWrapper(
      apiConfig?.priceQuoteUrl,
      payloadObject,
    );
    return response;
  } catch (error) {
    console.error("Error while fetching price:", error);
  }
};

export const getIsExistingCustomer = async (
  payload: IsReturningCustomerPayload,
): Promise<IsReturningCustomerResponse> => {
  try {
    const response = await globalThis.IsReturningCustomerWrapper(
      apiConfig?.isExistingCustomer,
      payload,
    );
    return response;
  } catch (error) {
    console.log("Error while making existing customer api call", error);
  }
};

export const getStates = async (countryCode: string = "US") => {
  try {
    const data = await globalThis.GetCachedUnifiedSync(
      `${apiConfig.stateApi}${countryCode}?apikey=${apiConfig.API_KEY}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching states:", error);
  }
};

export const fetchCESOffers = async (
  formValues: FormValues,
  offerFromCES?: boolean,
) => {
  const dbaName = localStorage.getItem("dbaName");
  const selectedServiceId = formValues?.serviceScenarioType?.serviceId;
  const payload: OffersRequestPayload = {
    conceptID: Number(formValues.conceptID),
    displayOffer: "Web",
    offerType: offerFromCES ? "Global" : "",
    webLocationID: Number(formValues.franchiseWebLocationId) || 0,
    dbaName: dbaName || "",
  };
  if (formValues?.validate_offers_with_service_categories) {
    payload.serviceCategories = formValues.serviceCategoryID || [];
  }
  if (selectedServiceId) {
    payload.serviceTypeID = Number(selectedServiceId);
  }
  try {
    const response = await globalThis.CouponOffersWrapper(
      apiConfig.serviceOffers,
      payload,
    );
    return response;
  } catch (error) {
    console.error("Error fetching offers:", error);
  }
};

export const validateOffer = async (
  ConceptID: string,
  CouponCode: string,
  WebLocationID: string,
  ServiceCategoryID?: string,
  validate_offers_with_service_categories: boolean = false,
  ServiceTypeID?: number,
): Promise<{ success: boolean; isCouponApplicable?: boolean }> => {
  const DbaName = localStorage.getItem("dbaName");
  let url = `${apiConfig.validateOfferApi}&ConceptID=${ConceptID}&CouponCode=${CouponCode}&WebLocationID=${WebLocationID}&DbaName=${DbaName}`;
  try {
    // Add optional parameters if provided
    if (ServiceCategoryID && validate_offers_with_service_categories) {
      url += `&ServiceCategoryID=${ServiceCategoryID}`;
    }
    if (ServiceTypeID) {
      url += `&ServiceTypeID=${ServiceTypeID}`;
    }
    const response = await globalThis.GetCachedUnifiedSync(url);
    if (
      response?.coupon === null &&
      response?.isCouponApplicable === false &&
      response?.isCouponAvailable === true
    ) {
      return {
        success: true,
        isCouponApplicable: response?.isCouponApplicable,
      };
    } else if (response?.coupon) {
      sessionStorage.setItem("offerDetails", JSON.stringify(response.coupon));
      globalThis.dispatchEvent(new Event("offerUpdate"));
      return {
        success: true,
        isCouponApplicable: response?.isCouponApplicable,
      };
    }
  } catch (error) {
    console.error("Coupon validation error:", error);
  }
  return { success: false };
};

export const calculateEstimate = async (
  formValues: FormValues,
  formConfig: SchedulerConfig,
  missingCityStateData: { city: string; state: string },
) => {
  const stateCode = stateAbbreviations[missingCityStateData.state]
    ? stateAbbreviations[missingCityStateData.state]
    : missingCityStateData.state;
  const payload: CalculateEstimatePayload = {
    firstName: formValues.fname,
    lastName: formValues.lname,
    address: formValues.address1 ? formValues.address1 : formValues.address,
    address2: formValues.address2 ? formValues.address2 : "",
    city: missingCityStateData.city,
    state: stateCode,
    webLocationId: Number(formValues.franchiseWebLocationId),
    postalCode: formValues.zipcode,
    email: formValues.email,
    phone: formValues.phone,
    estimateSquareFeet: formValues[MLY_IDS.MLY_HOME_PROPERTY_SQ_FT],
    estimateBedrooms: formValues[MLY_IDS.MLY_RES_BEEDROOMS],
    estimateBathrooms: formValues[MLY_IDS.MLY_RES_BATHROOMS],
    conceptId: formConfig.conceptid,
    vendorId: Number(formConfig.vendorId),
    licenseNumber: formValues.licenseNumber,
  };
  try {
    const response = globalThis.CalcEstimate(payload);
    return response;
  } catch (error) {
    console.error("Error while fetching estimate:", error);
  }
};

export const fetchLawnCareServices = async (request: {
  webLocationId?: number;
  dbaName?: string;
  conceptId?: number;
  serviceModifier?: string;
}) => {
  try {
    const url = `${apiConfig.trendingServicesApi}?ConceptId=${request.conceptId}&WebLocationId=${request.webLocationId}&DbaName=${request.dbaName}&ServiceModifier=${request.serviceModifier}`;
    const response = await globalThis.GetCachedResponse(url);
    return response?.trendingServices;
  } catch (error) {
    console.error("Error fetching trending services:", error);
    throw error;
  }
};

export const gardenAreaLookup = async (payload) => {
  try {
    const data = await axios.post(apiConfig.gardenAreaLookup, payload, {
      headers: {
        "Content-Type": "application/json",
        apikey: apiConfig.API_KEY,
      },
    });

    return {
      status: data?.data?.status,
      data: data?.data,
      error: false,
    };
  } catch (error) {
    console.error("Error fetching garden area:", error);
    return {
      error: true,
      message: "Error fetching garden area",
      data: null,
    };
  }
};

export const initImgUpload = async (payload) => {
  try {
    const res = await axios.post(apiConfig.initImgUpload, payload, {
      headers: {
        "Content-Type": "application/json",
        apikey: apiConfig.WRAPPER_API_KEY,
      },
    });
    return res.data;
  } catch (error) {
    console.log(error);
  }
};

export const uploadImgS3Bucket = async (uploadUrl, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file: ${response.statusText}`);
    }
    return response;
  } catch (error) {
    console.error("Error uploading file:", error);
  }
};

export const uploadcompleteImg = async (uploadKey) => {
  await axios.post(apiConfig.uploadcomplete, uploadKey, {
    headers: {
      "Content-Type": "application/json",
      apikey: apiConfig.WRAPPER_API_KEY,
    },
  });
};
